/** @jsxImportSource @emotion/react */
import { compactFlatten, present } from '@art-suite/art-core-ts';
import { css } from '@emotion/react';
import React from 'react';
import { LAYOUT2, TYPOGRAPHY2 } from '../Styles';
import { ColumnsLayout } from './ColumnsLayout';
import { LabeledCard } from './LabeledCard';
import { LabelValueRow } from './LabelValueRow';

const DETAILS_CARD_STYLE = css`
  label: DETAILS_CARD_STYLE;
  min-width: 0;
`;

const COLUMN_STYLE = css`
  gap: ${LAYOUT2.spacing1}px;
`;

const PROPS_VALUE_STYLE = css`
  label: PROPS_VALUE_STYLE;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${LAYOUT2.spacing1}px;
  color: var(--text2);
`;

const PROPS_CARD_LABEL_STYLE = css`
  label: PROPS_CARD_LABEL_STYLE;
  ${TYPOGRAPHY2.xSmallBold}
  flex: 1;
  color: var(--text);
`;

const PROPS_VALUE_PLACEHOLDER_STYLE = css`
  label: PROPS_VALUE_PLACEHOLDER_STYLE;
  color: var(--text2);
  font-style: italic;
`;

const ITEM_STYLE = css`
  label: ITEM_STYLE;
  display: flex;
  width: 100%;
  flex-direction: row;
  gap: ${LAYOUT2.spacing1}px;
`;

export const PropsCard = ({ items, columnCount = 1, alignLabels = 'top', uppercaseLabel = false, ...labeledCardProps }: {
  items: Array<{ label?: string; value?: React.ReactNode } | undefined | false | React.ReactElement>;
  columnCount?: number;
  alignLabels?: 'top' | 'center';
  uppercaseLabel?: boolean;
} & Omit<Parameters<typeof LabeledCard>[0], 'children'>) => {
  return (
    <LabeledCard {...{ height: '100%', ...labeledCardProps }}>
      <ColumnsLayout
        items={compactFlatten(items).filter(present)}
        columnCount={columnCount}
        columnStyle={[COLUMN_STYLE, alignLabels === 'center' ? css`align-items: center;` : css`align-items: flex-start;`]}
        fillDirection='column-first'
        renderItem={(item, index) => {
          if (React.isValidElement(item)) return <div key={index} css={ITEM_STYLE}>{item}</div>;
          if (!item) return null;
          const { label, value } = item;
          if (present(value) && (typeof value === 'string' || typeof value === 'number')) {
            return <LabelValueRow key={index} label={label ?? ''} value={String(value)} uppercaseLabel={uppercaseLabel} />;
          }
          return (
            <div key={index} css={ITEM_STYLE}>
              {label ? <div css={PROPS_CARD_LABEL_STYLE}>{label}</div> : null}
              {value
                ? (
                  <div css={PROPS_VALUE_STYLE}>
                    {present(value) ? value : <span css={PROPS_VALUE_PLACEHOLDER_STYLE}>-</span>}
                  </div>
                )
                : null}
            </div>
          );
        }}
      />
    </LabeledCard>
  );
};
